/**
 * A tail-promise queue for every caller that shows a Primer `useConfirm` dialog from a
 * client-tool fulfilment. Client-tool fulfilments can run concurrently (e.g. `create_session`
 * and `create_pull_request` in the same turn), and Primer's `useConfirm` mounts a fresh modal
 * root per call — without a shared queue, two concurrent confirmations would stack dialogs and
 * focus traps. Chaining every confirmation on one tail promise ensures only one dialog is
 * mounted at a time.
 */
export interface ConfirmDialogQueue {
  /** Runs `show` only after every previously queued confirmation on this queue has settled. */
  queueConfirmDialog: <T>(show: () => Promise<T>) => Promise<T>
}

/** Builds an independent queue — use this when a caller must not share state with others. */
export function createConfirmDialogQueue(): ConfirmDialogQueue {
  let tail: Promise<unknown> = Promise.resolve()
  return {
    queueConfirmDialog<T>(show: () => Promise<T>): Promise<T> {
      const previous = tail
      const result = (async () => {
        try {
          await previous
        } catch {
          // Ignore how the previous confirmation settled; just wait our turn.
        }
        return show()
      })()
      tail = (async () => {
        try {
          await result
        } catch {
          // Keep the queue alive regardless of this confirmation's outcome.
        }
      })()
      return result
    },
  }
}

/**
 * One shared, module-level queue for callers that must coordinate across the whole app (e.g.
 * two different tools whose confirmations may genuinely race each other). Most callers want
 * {@link createConfirmDialogQueue} instead, scoped to their own hook/component, so they don't
 * take on cross-feature coupling they don't need.
 */
export const sharedConfirmDialogQueue: ConfirmDialogQueue = createConfirmDialogQueue()

/** Convenience export — equivalent to `sharedConfirmDialogQueue.queueConfirmDialog`. */
export const queueConfirmDialog = sharedConfirmDialogQueue.queueConfirmDialog
